import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronsUpDown,
  Plus,
  Building2,
  BadgeCheck,
  Loader2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import {
  useListClubs,
  useCreateClub,
  getListClubsQueryKey,
} from "@workspace/api-client-react";
import type { Club } from "@workspace/api-client-react";

interface ClubPickerProps {
  value: string | null | undefined;
  onChange: (clubId: string | null) => void;
  country: string | null | undefined;
  disabled?: boolean;
  placeholder?: string;
  testId?: string;
  className?: string;
}

function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function ClubPicker({
  value,
  onChange,
  country,
  disabled,
  placeholder = "Selecciona un club",
  testId = "club-picker",
  className,
}: ClubPickerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 250);
  const [creating, setCreating] = useState(false);
  const [newClubName, setNewClubName] = useState("");
  const [newClubCity, setNewClubCity] = useState("");

  const upperCountry = country ? country.toUpperCase() : null;
  const validCountry = !!upperCountry && upperCountry.length === 2;

  const { data: selectedListData } = useListClubs(
    { country: upperCountry ?? "XX" },
    {
      query: {
        queryKey: getListClubsQueryKey({ country: upperCountry ?? "XX" }),
        enabled: !!value && validCountry,
        staleTime: 60_000,
      },
    },
  );
  const selectedClub: Club | null = useMemo(() => {
    if (!value || !selectedListData) return null;
    return (
      (selectedListData as Club[]).find((c) => c.id === value) ?? null
    );
  }, [selectedListData, value]);

  const listParams = {
    country: upperCountry ?? "XX",
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
  };
  const { data: clubs = [], isFetching } = useListClubs(listParams, {
    query: {
      queryKey: getListClubsQueryKey(listParams),
      enabled: open && validCountry,
    },
  });

  const sortedClubs = useMemo(() => {
    return [...(clubs as Club[])].sort((a, b) => {
      const av = a.verified ? 0 : 1;
      const bv = b.verified ? 0 : 1;
      if (av !== bv) return av - bv;
      return a.name.localeCompare(b.name);
    });
  }, [clubs]);

  const createClub = useCreateClub({
    mutation: {
      onSuccess: (club) => {
        queryClient.invalidateQueries({
          queryKey: getListClubsQueryKey().slice(0, 1),
        });
        onChange(club.id);
        setCreating(false);
        setNewClubName("");
        setNewClubCity("");
        setOpen(false);
        toast({
          title: club.verified ? "Club seleccionado" : "Club añadido",
          description: club.verified
            ? `Has elegido ${club.name}.`
            : `${club.name} se añadió y queda pendiente de verificación.`,
        });
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error ? err.message : "No se pudo crear el club";
        toast({
          title: "Error",
          description: message,
          variant: "destructive",
        });
      },
    },
  });

  const displayLabel = selectedClub
    ? `${selectedClub.name}${selectedClub.city ? ` · ${selectedClub.city}` : ""}`
    : value
      ? "Club seleccionado"
      : placeholder;

  return (
    <div className={cn("space-y-1", className)} data-testid={testId}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setCreating(false);
            setSearch("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={disabled || !validCountry}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground",
            )}
            data-testid={`${testId}-trigger`}
          >
            <span className="flex items-center gap-2 truncate">
              <Building2 className="h-4 w-4 shrink-0 opacity-60" />
              <span className="truncate">{displayLabel}</span>
              {selectedClub?.verified && (
                <BadgeCheck className="h-4 w-4 text-primary shrink-0" />
              )}
            </span>
            <span className="flex items-center gap-1 ml-2 shrink-0">
              {value && !disabled && (
                <span
                  role="button"
                  tabIndex={0}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(null);
                    }
                  }}
                  className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                  data-testid={`${testId}-clear`}
                  aria-label="Quitar club"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]"
          align="start"
        >
          {!validCountry ? (
            <div className="p-4 text-sm text-muted-foreground">
              Selecciona primero un país para buscar clubes.
            </div>
          ) : creating ? (
            <div className="p-3 space-y-3" data-testid={`${testId}-create-form`}>
              <div className="space-y-1">
                <Label htmlFor={`${testId}-new-name`} className="text-xs">
                  Nombre del club *
                </Label>
                <Input
                  id={`${testId}-new-name`}
                  autoFocus
                  value={newClubName}
                  onChange={(e) => setNewClubName(e.target.value)}
                  placeholder="Ej: Club Padel Central"
                  data-testid={`${testId}-new-name-input`}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor={`${testId}-new-city`} className="text-xs">
                  Ciudad
                </Label>
                <Input
                  id={`${testId}-new-city`}
                  value={newClubCity}
                  onChange={(e) => setNewClubCity(e.target.value)}
                  placeholder="Ciudad"
                  data-testid={`${testId}-new-city-input`}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Tu club quedará pendiente de verificación por un administrador.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setCreating(false);
                    setNewClubName("");
                    setNewClubCity("");
                  }}
                  data-testid={`${testId}-cancel-create`}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={
                    createClub.isPending || newClubName.trim().length < 2
                  }
                  onClick={() =>
                    createClub.mutate({
                      data: {
                        name: newClubName.trim(),
                        country: upperCountry!,
                        city: newClubCity.trim() || null,
                      },
                    })
                  }
                  data-testid={`${testId}-confirm-create`}
                >
                  {createClub.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Añadir"
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Buscar club..."
                value={search}
                onValueChange={setSearch}
                data-testid={`${testId}-search`}
              />
              <CommandList>
                {isFetching && (
                  <div className="flex items-center justify-center py-3 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin mr-2" />
                    Buscando...
                  </div>
                )}
                {!isFetching && sortedClubs.length === 0 && (
                  <CommandEmpty>No se encontraron clubes.</CommandEmpty>
                )}
                {sortedClubs.length > 0 && (
                  <CommandGroup heading="Clubes">
                    {sortedClubs.map((club) => (
                      <CommandItem
                        key={club.id}
                        value={club.id}
                        onSelect={() => {
                          onChange(club.id);
                          setOpen(false);
                          setSearch("");
                        }}
                        data-testid={`${testId}-option-${club.id}`}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === club.id ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate font-medium">
                              {club.name}
                            </span>
                            {club.verified && (
                              <BadgeCheck className="h-3.5 w-3.5 text-primary shrink-0" />
                            )}
                          </div>
                          {club.city && (
                            <p className="text-[11px] text-muted-foreground truncate">
                              {club.city}
                            </p>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                <CommandGroup>
                  <CommandItem
                    value="__add_new_club__"
                    onSelect={() => {
                      setNewClubName(search.trim());
                      setCreating(true);
                    }}
                    data-testid={`${testId}-add-new`}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Añadir mi club
                    {search.trim() && (
                      <span className="ml-1 text-xs text-muted-foreground truncate">
                        "{search.trim()}"
                      </span>
                    )}
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
